<script setup lang="ts">
    import {useI18n} from 'vue-i18n';
    import {ref} from 'vue';
    import useCpBem from '~/composables/useCpBem';
    import CpText from '~/components/cp/CpText/CpText.vue';
    import CpIconEmptyState from '~/components/cp/CpIcon/CpIconEmptyState/CpIconEmptyState.vue';
    import CpButton from '~/components/cp/CpButton/CpButton.vue';
    import {useCpUser} from '~/composables/useCpUser';
    import {usePdp} from '~/stores/detailsStore';
    import CpModalDialog from '~/components/cp/CpModal/CpModalDialog/CpModalDialog.vue';

    defineProps<{
      reviewed: boolean
    }>();
    const productStore = usePdp();
    /** Modal **/
    const showModalRequieredLogin = ref(false);
    /** Authenticated flow **/
    const {isAuthenticated} = useCpUser();
    function isAuth() {
      // Authenticated case is handled by the button
      if (!isAuthenticated.value) {
        showModalRequieredLogin.value = true;
      }
    }
    function showModalLogin() {
      showModalRequieredLogin.value = false;
      useEvent('show-login-modal',
        {
          show: true,
          route: 'details-write-new-review',
          query: {cl: 'cpreviews_write', anid: productStore.getProductId},
        });
    }

    const {b, e} = useCpBem('cp-card-redirect-reviews');
    const {t} = useI18n();
    const writeReviewRoute = isAuthenticated.value ?
      {name: 'details-write-new-review' as const, query: {cl: 'cpreviews_write', anid: productStore.getProductId}} :
      undefined;
</script>

<template>
    <div :class="b">
        <CpModalDialog
            v-model:show="showModalRequieredLogin"
            :title="t('warning')"
            :body="t('forYouCanWrite')"
            :label-first-button="t('login')"
            type-first-button="primary"
            @first-button="showModalLogin()"
        />
        <div :class="e`title`">
            <CpText v-if="reviewed" variant="heading-h3">
                {{ t('thanks') }}
            </CpText>
            <CpText v-else variant="heading-h3">
                {{ t('opinion') }}
            </CpText>
        </div>
        <div :class="e`message-box`">
            <CpIconEmptyState icon="grade" />
            <CpText v-if="reviewed" variant="body-regular">
                {{ t('thankYouText') }}
            </CpText>
            <CpText v-else variant="body-regular">
                {{ t('opinionText') }}
            </CpText>
        </div>
        <div :class="e`cta`">
            <CpButton
                v-if="reviewed"
                :label="t('write')"
                kind="label"
                type="button"
                full-width
                disabled
            />
            <CpButton
                v-else
                :label="t('write')"
                :href="isAuthenticated ? productStore.getProduct?.writeReviewLink : undefined"
                kind="label"
                type="button"
                data-testid="cp-card-redirect-reviews-write__write-button"
                full-width
                color="primary"
                @click="isAuth"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .cp-card-redirect-reviews {
      border: 1px solid var(--color-neutral-extra-light);
      border-radius: 8px;
      &__title {
        padding: 16px;
        border-bottom: 1px solid var(--color-neutral-extra-light);
      }
      &__message-box {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        gap: 16px;
        padding: 16px;
        border-bottom: 1px solid var(--color-neutral-extra-light);
      }
      &__cta {
        padding: 16px;
      }
    }
</style>

<i18n locale="es-US" lang="yaml">
    write: "Escribir opinión"
    opinion: "¿Y tú qué opinas?"
    thanks: "Gracias por tu opinión"
    opinionText: "Si tienes experiencia usando este producto danos tu opinión."
    thankYouText: "Muchas gracias por participar en nuestra comunidad. Solamente puedes escribir una opinión por producto."
    warning: 'Aviso'
    forYouCanWrite: 'Para que puedas escribir tu opinión es necesario que inicies sesión.'
    login: 'Iniciar sesión'
</i18n>
